"use client";

import { useEffect, useState } from "react";
import { X, Send } from "lucide-react";
import type { SiteSettings } from "@/lib/types";

interface CommunityPopupProps {
  settings: SiteSettings;
}

/** Mini rectangular card nudging visitors to join the community channel. */
export default function CommunityPopup({ settings }: CommunityPopupProps) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    // Show shortly after load so it doesn't compete with the page pop-in
    const t = setTimeout(() => {
      setVisible(true);
      requestAnimationFrame(() => setAnimating(true));
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const closeCard = () => {
    setAnimating(false);
    setTimeout(() => setVisible(false), 300);
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed bottom-4 left-4 right-4 sm:right-auto sm:w-[340px] z-40 transform transition-all duration-300 ${
        animating
          ? "translate-y-0 opacity-100"
          : "translate-y-6 opacity-0 pointer-events-none"
      }`}
      role="complementary"
      aria-label="Join our community"
    >
      <div className="relative bg-zinc-900/90 backdrop-blur-md border theme-accent-border-soft rounded-2xl p-4 shadow-[0_8px_30px_rgba(0,0,0,0.5)] overflow-hidden">
        {/* Soft accent glow inside the card */}
        <div className="absolute -top-8 -left-8 w-24 h-24 theme-logo-glow-soft rounded-full blur-[24px] pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={closeCard}
          className="absolute top-2.5 right-2.5 text-zinc-500 hover:text-white transition-colors p-1 rounded-md hover:bg-zinc-800/60"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="relative flex items-center gap-3">
          {/* Logo */}
          <div className="shrink-0 w-11 h-11 bg-zinc-950 rounded-xl p-0.5 border theme-accent-border-mid overflow-hidden">
            <img
              src="/logo.png"
              alt="Study Bison Logo"
              className="w-full h-full object-cover rounded-[10px]"
            />
          </div>

          {/* Text */}
          <div className="min-w-0 pr-5">
            <h3 className="text-white font-serif-premium font-bold text-[14px] leading-tight tracking-wide">
              Join our Community
            </h3>
            <p className="text-zinc-400 text-[11px] leading-snug mt-0.5">
              This site can be banned anytime — get updates & support on our
              channel.
            </p>
          </div>
        </div>

        {/* Action Button */}
        <a
          href={settings.communityUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeCard}
          className="relative mt-3 w-full py-2.5 bg-[#2AABEE] hover:bg-[#2298d6] text-white font-bold text-[11px] uppercase tracking-widest rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(42,171,238,0.3)] hover:scale-[1.02]"
        >
          {settings.communityLabel}
          <Send className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}
